import { Paper } from '@mui/material';
import Grid from '@mui/material/Grid'; // MUI v7 Grid (Grid2 API)
import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import 'react-grid-layout/css/styles.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ButtonVitec from '../../../app/components/ButtonVitec';
import Utils from '../../../felles/Utils';
import MenuItemTarget from '../../../models/MenuItemTarget';
import ApiResponse from '../../../models/payload/APIResponse';
import UISize from '../../../models/UISize';
import * as actions from '../../../store/actions/index';
import * as actionsNN05 from '../../../store/actions/indexNN05';
import { AppState } from '../../../store/rootReducer';
import { useStyles } from '../styles/Dialog.styles';


/**
 * NN05Dialog Component
 * 
 * A modal dialog component used within the NN05 employee management module.
 * This dialog displays feedback messages and responses from API operations
 * such as employee creation, updates, and deletions.
 *
 * Author: fabrice.miras@vitecsoftware.com
 * Date: 05.08.2025
 *  
 * Features:
 * - Modal overlay with focus management
 * - Keyboard navigation support (Enter, F3, Shift+End)
 * - Responsive design with sandwich menu support
 * - Auto-focus on mount for accessibility
 * - Redux state management integration
 * 
 * @component
 * @example
 * // Used automatically when transDialogEmployeeFeedback state is set
 * // The dialog will display the message from the API response
 * 
 * @returns {JSX.Element} The NN05Dialog component
 */
export default function NN05Dialog(/*props*/) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const screenSize: UISize = useSelector(state => (state as AppState).ui.screenSize);
  const open = screenSize.width > 0 && screenSize.width < 1200 ? false : true;
  const navigate = useNavigate();
  const n: MenuItemTarget = useSelector((state) => (state as AppState).menuItem.menuItemTarget);
  const isResetFormEnabled: boolean = useSelector((state) => (state as AppState).NN05.isResetFormEnabled);
  const transDialogEmployeeFeedback: ApiResponse = useSelector((state) => (state as AppState).NN05.transDialogEmployeeFeedback);
  const dialogRef = useRef<HTMLDivElement>(null);
  const button: boolean = useSelector((state) => (state as AppState).ui.isSandwichButtonTurnedOn);
  const transStateDialogEmployee: number = useSelector((state) => (state as AppState).NN05.transStateDialogEmployee);
  /**
   * Effect hook that handles component mounting and cleanup
   * 
   * - Sets focus to the dialog for accessibility
   * - Adds keyboard event listener for navigation
   * - Cleans up event listener on unmount
   * - Resets form if reset flag is enabled
   * 
   * Dependencies: dispatch, isResetFormEnabled, transDialogEmployeeFeedback
   */
  useEffect(() => {
    dialogRef.current?.focus();
    document.addEventListener('keydown', handlePressedKey);
    // On exit
    return () => {

      document.removeEventListener('keydown', handlePressedKey);
      if (isResetFormEnabled)
        dispatch(actionsNN05.NN05ResetForm(true));

      //dispatch(actionsNN05.setTransStateDialogEmployee(0));
    };

  }, [dispatch, isResetFormEnabled, transDialogEmployeeFeedback]);
  /**
   * Handles keyboard events for dialog navigation and actions
   * 
   * Supported keyboard shortcuts:
   * - Shift+F7, F8: Prevented (reserved for other actions)
   * - Shift+End, F3: Closes dialog and navigates back to menu
   * - Enter: Confirms dialog and closes it
   * 
   * @param {KeyboardEvent | React.KeyboardEvent<HTMLDivElement>} event - The keyboard event object
   */
  const handlePressedKey = (
    event: KeyboardEvent | React.KeyboardEvent<HTMLDivElement>
  ) => {
    const key = 'key' in event ? event.key : undefined;
    //
    if ((event.shiftKey && key === 'F7') || key === 'F8') {
      event.preventDefault();
    }
    if ((event.shiftKey && key === 'End') || key === 'F3') {
      event.preventDefault();
      //  Reset form and component store
      dispatch(actionsNN05.NN05ClearComponentStore());
      //
      dispatch(
        actions.menuItemSetTarget(
          new MenuItemTarget(
            n.rootItem,
            n.item !== '' ? n.item : n.rootItem,
            '',
            '',
            n.tilpasset,
            n.prodSynlig,
            n.kodeSynlig,
            n.labelSynlig,
            n.menuSynlig,
            n.hasChildren,
            n.title
          )
        )
      );
      dispatch(actions.menuItemSetSelected([n.rootItem, n.item + n.rootItem]));
      navigate(Utils.HOMEPAGE_CONTEXT + '/menu');
    }
    if (key === 'Enter') {
      event.preventDefault();
      handleOK();
    }
  };
  /**
   * Handles the OK button click and dialog closure
   * 
   * Resets all transaction states to 0:
   * - Dialog state (closes the dialog)
   * - Delete operation state
   * - Update operation state
   * 
   * This effectively closes the dialog and clears any pending operations.
   */
  const handleOK = () => {
    dispatch(actionsNN05.setTransStateDialogEmployee(0));
    dispatch(actionsNN05.setTransStateDeleteEmployee(0));
    dispatch(actionsNN05.setTransStateUpdateEmployee(0));
    dispatch(actionsNN05.setLockKeypressed(1));
  };
  //
  return (
    <div className={classes.component}>
      <div className={classes.bg} />
      <div className={clsx(classes.content, { [classes.contentShift]: button ? true : open })}>
        <div ref={dialogRef} className={classes.root} tabIndex={0} onKeyDown={(e) => handlePressedKey(e)} >
          <Paper elevation={10} sx={{ height: '200px', width: 'auto', zIndex: 10002, padding: 2, alignItems: 'center', justifyContent: 'center' }} >
            {/* Content of the dialog */}
            <Grid container direction="column" >
              {/* Header */}
              <Grid sx={{
                flex: '0 0 auto', padding: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 80,
              }}>
                {/* Place your header content here */}
                <h2 style={{ margin: 0, alignSelf: 'center' }}>{transDialogEmployeeFeedback.message}</h2>
              </Grid>
              <Grid sx={{
                flex: '0 0 auto', marginTop: 'auto', padding: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5, height: 60,
              }}>
                {/* Place your footer actions here */}
                <ButtonVitec _label="OK" _type="submit" _padding={0} _onclick={() => { handleOK() }} />
              </Grid>
            </Grid>
          </Paper>
        </div>
      </div>
    </div>
  );
}